import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = {
    timeoutMs: { type: Number, default: 55000 },
  }

  connect() {
    this.timeoutId = null
    this.abortController = null
    this.timedOut = false

    this.boundBeforeFetchRequest = this.handleBeforeFetchRequest.bind(this)
    this.boundFrameLoad = this.handleFrameLoad.bind(this)
    this.boundFrameMissing = this.handleFrameMissing.bind(this)

    document.addEventListener(
      'turbo:before-fetch-request',
      this.boundBeforeFetchRequest,
    )
    this.element.addEventListener('turbo:frame-load', this.boundFrameLoad)
    document.addEventListener('turbo:frame-missing', this.boundFrameMissing)
  }

  disconnect() {
    this.clearTimer()
    this.abortInFlightRequest()

    document.removeEventListener(
      'turbo:before-fetch-request',
      this.boundBeforeFetchRequest,
    )
    this.element.removeEventListener('turbo:frame-load', this.boundFrameLoad)
    document.removeEventListener('turbo:frame-missing', this.boundFrameMissing)
  }

  handleBeforeFetchRequest(event) {
    const headers = event.detail?.fetchOptions?.headers
    const turboFrameHeader =
      headers?.['Turbo-Frame'] || headers?.['turbo-frame']

    if (turboFrameHeader !== this.element.id) return

    this.timedOut = false
    this.clearTimer()
    this.abortInFlightRequest()

    this.abortController = new AbortController()
    event.detail.fetchOptions.signal = this.abortController.signal

    this.startTimer()
    this.enableRelatedFormControls()
  }

  handleFrameLoad(event) {
    if (event.target !== this.element) return

    this.timedOut = false
    this.clearTimer()
    this.abortController = null
    this.enableRelatedFormControls()
  }

  handleFrameMissing(event) {
    if (event.target !== this.element) return

    event.preventDefault()

    if (!this.timedOut) {
      this.renderTimeoutState()
    }
  }

  startTimer() {
    this.clearTimer()

    this.timeoutId = window.setTimeout(() => {
      this.renderTimeoutState()
    }, this.timeoutMsValue)
  }

  clearTimer() {
    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId)
      this.timeoutId = null
    }
  }

  abortInFlightRequest() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  renderTimeoutState() {
    if (this.timedOut) return

    this.timedOut = true
    this.clearTimer()
    this.abortInFlightRequest()

    this.element.innerHTML = `
      <div class="relative z-20 min-h-[200px] flex items-center justify-center py-8">
        <div class="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center shadow-sm">
          <p class="mb-2 text-base font-medium text-amber-900">
            This request is taking longer than expected.
          </p>
          <p class="text-sm text-amber-800">
            Try a shorter date range, select a product, or narrow to a specific CSM.
          </p>
        </div>
      </div>
    `

    this.element.removeAttribute('busy')
    this.enableRelatedFormControls()
  }

  enableRelatedFormControls() {
    const frameId = this.element.id
    if (!frameId) return

    document
      .querySelectorAll(
        `form[data-turbo-frame="${frameId}"] button, form[data-turbo-frame="${frameId}"] input, form[data-turbo-frame="${frameId}"] select, a[data-turbo-frame="${frameId}"]`,
      )
      .forEach((el) => {
        if ('disabled' in el) el.disabled = false
        el.removeAttribute('aria-disabled')
      })
  }
}
